import React, { useContext } from 'react';
import { BinContext } from '../../../../context/BinContext';
import { ProductContext } from '../../../../context/ProductContext';
import { StockLocationContext } from '../../../../context/StockLocationContext';
import Provider from '../../../../providers/common/Provider';
import { OnboardingLS } from '../../../../util/local_storage/onboarding.ls';
import { Button } from '../../../components/Button';
import AppStoreButton from '../../../components/Button/templates/AppStoreButton';
import GooglePlayStoreButton from '../../../components/Button/templates/GooglePlayStoreButton';
import { Modal, ModalTitle } from '../../../components/Modal';
import Icon, { IconType } from '../../../icons/Icon';

interface OnboardingCompleteModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export default function OnboardingCompleteModal(props: OnboardingCompleteModalProps) {
  return (
    <Provider stockLocation bin product>
      <Element {...props} />
    </Provider>
  );
}

function Element({ open, setOpen }: OnboardingCompleteModalProps) {
  const { stockLocations } = useContext(StockLocationContext);
  const { bins } = useContext(BinContext);
  const { products } = useContext(ProductContext);

  const state = OnboardingLS.getOnboardedState();

  return (
    <Modal open={open} setOpen={setOpen}>
      <div className='grid gap-6 px-8 py-6'>
        <div className='grid gap-1'>
          <div className='flex h-fit w-full justify-end'>
            <Icon type={IconType.xClose} stroke={'stroke-gray-400'} onClick={() => setOpen(false)} />
          </div>

          <ModalTitle text={'Welcome to Ventory'} />
          <p className='text-sm text-gray-600'>
            Your inventory is now live! You can start exploring, managing stock, and optimizing your operations right
          </p>
        </div>
        <div className='h-[2px] w-full bg-gray-300'></div>
        <div className='grid gap-3'>
          <p className='text-sm text-gray-600'>
            Here's what we've added to your company's inventory based on your uploaded data:
          </p>
          <p className='text-sm font-medium text-black'>Ventory HQ inventory</p>
          <div className='grid grid-cols-3 gap-3 stroke-gray-600'>
            <div className='grid rounded-2 border border-solid border-gray-300'>
              <div className='grid gap-4 px-3 py-4'>
                <Icon type={IconType.markerPin1} />
                <div>
                  <p className='text-black'>{stockLocations.size}</p>
                  <p className='text-xs text-gray-600'>Stock Locations</p>
                </div>
              </div>
              <div className='rounded-b-2 border-b-[10px] border-b-primary'></div>
            </div>
            <div className='grid rounded-2 border border-solid border-gray-300'>
              <div className='grid gap-4 px-3 py-4'>
                <Icon type={IconType.package} />
                <div>
                  <p className='text-black'>{products.size}</p>
                  <p className='text-xs text-gray-600'>Products</p>
                </div>
              </div>
              <div className='rounded-b-2 border-b-[10px] border-b-primary'></div>
            </div>
            <div className='grid rounded-2 border border-solid border-gray-300'>
              <div className='grid gap-4 px-3 py-4'>
                <Icon type={IconType.container} />
                <div>
                  <p className='text-black'>{bins.size}</p>
                  <p className='text-xs text-gray-600'>Bins</p>
                </div>
              </div>
              <div className='rounded-b-2 border-b-[10px] border-b-primary'></div>
            </div>
          </div>
        </div>
        <div className='h-[2px] w-full bg-gray-300'></div>
        {state === 'DEFAULT' ? (
          <div className='grid gap-1 text-sm text-black'>
            <p className='font-medium'>Manage your inventory anytime, anywhere!</p>
            <p>Download the Ventory mobile app for seamless access and real-time updates on the go.</p>
            <p className='font-medium'>Available for iOS and Android. </p>
          </div>
        ) : (
          <div className='grid gap-2 text-sm text-black'>
            <div>
              <p className='font-medium'>Start testing the Ventory mobile app</p>
              <p className='text-gray-600'>Try it out with demo data - no setup needed!</p>
            </div>
            <div className='grid gap-3'>
              <p>
                Step 1:{' '}
                <a
                  href={`${process.env.REACT_APP_STORAGE_URL}/ventory/demo_labels.zip`}
                  className='inline-flex items-center text-blue-600'
                >
                  Download labels for scanning{' '}
                  <span className='ml-1 inline-block'>
                    <Icon type={IconType.download1} stroke={'stroke-black'} size={'h-4 w-4'} />
                  </span>
                </a>
              </p>
              <p>Step 2: Download the Ventory mobile app and start scanning like a pro.</p>
            </div>
          </div>
        )}
        <div className='flex gap-2'>
          <GooglePlayStoreButton
            onClick={() => window.open('https://play.google.com/store/apps/details?id=com.ventoryapp.wms')}
          />
          <AppStoreButton onClick={() => window.open('https://apps.apple.com/app/ventory/id1538184839')} />
        </div>

        <div className='flex h-fit w-full justify-end'>
          <Button text={'Get started'} onClick={() => setOpen(false)} />
        </div>
      </div>
    </Modal>
  );
}
